import { api } from "../api/client.ts";
import type { PageIssue, Severity } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { commentCount } from "../lib/commentCounts.ts";
import { useEntities } from "../lib/data.ts";
import { useItemPicks } from "../lib/itemPicks.ts";
import { KINDS } from "../lib/itemQuery.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { useListScreen } from "../lib/remember.ts";
import { useResource } from "../lib/resource.ts";
import { canEdit } from "../lib/roles.ts";
import { location, setQuery } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { Link } from "../ui/Button.tsx";
import { CommentCount, SeverityLabel } from "../ui/counts.tsx";
import { Select } from "../ui/form.tsx";
import { Async, EmptyState } from "../ui/misc.tsx";
import { Paged } from "../ui/Pager.tsx";
import { LINK } from "../ui/styles.ts";
import { Table, Td, Th } from "../ui/Table.tsx";
import { Toolbar } from "../ui/Toolbar.tsx";
import { RichText } from "./comments/richText.tsx";
import { FindingActions } from "./item/findingActions.tsx";
import { ItemLines } from "./items/icons.tsx";
import { LevelFilter } from "./items/levelFilter.tsx";
import { ItemsNav } from "./items/nav.tsx";

const PAGE_SIZE = 50;

/** Every open issue (schema violations and reviewers' findings) across items, or one entity's. */
export function Issues({ space }: PageProps) {
  const sp = location.value.searchParams;
  useListScreen("issues", space.id);
  const entities = useEntities(space);
  const entity = entities.data?.find((e) => e.id === sp.get("entity"));
  const list = useResource(`issues:${space.id}?${sp.toString()}`, (signal) =>
    api.get<PageIssue>(
      spaceApi(space, "/issues"),
      {
        entity: sp.get("entity"),
        kind: sp.get("kind"),
        severity: sp.get("severity"),
        source: sp.get("source"),
        cursor: sp.get("cursor"),
        limit: PAGE_SIZE,
      },
      signal,
    ),
  );
  // What each listed item is (its kind or file), for its icon: asked for together, as text links ask.
  const pick = useItemPicks(space, [...new Set((list.data?.data ?? []).map((x) => x.item))]);
  return (
    <>
      <ItemsNav
        space={space}
        tab="issues"
        entity={entity}
        title={sp.get("entity") === "none" ? t("items.noEntity") : undefined}
      />
      {/* One line on a phone too: the severity stays, the kind and then the source go into "⋯". */}
      <div class="mb-4 flex">
        <Toolbar
          items={[
            {
              key: "kind",
              node: (
                <div class="w-44">
                  <Select
                    aria-label={t("items.kind")}
                    value={sp.get("kind") ?? ""}
                    onValue={(v) => setQuery({ kind: v || null, cursor: null })}
                    options={[
                      { value: "", label: t("items.anyKind") },
                      ...KINDS.map((k) => ({ value: k, label: t(`items.kindFilter.${k}`) })),
                    ]}
                  />
                </div>
              ),
              drop: 20,
            },
            {
              key: "severity",
              // At least a level; the widest (notes too) is all of them.
              node: (
                <LevelFilter
                  label={t("issues.severity")}
                  value={(sp.get("severity") ?? "info") as Severity}
                  onPick={(v) =>
                    setQuery({ severity: v === "info" || v === sp.get("severity") ? null : v, cursor: null })
                  }
                  name={(s) => (s === "info" ? t("issues.allSeverities") : t(`issues.min.${s}`))}
                />
              ),
            },
            {
              key: "source",
              node: (
                <div class="w-56">
                  <Select
                    aria-label={t("issues.source")}
                    value={sp.get("source") ?? ""}
                    onValue={(v) => setQuery({ source: v, cursor: null })}
                    options={[
                      { value: "", label: t("issues.anySource") },
                      { value: "schema", label: t("issues.fromSchema") },
                      { value: "review", label: t("issues.fromReviews") },
                    ]}
                  />
                </div>
              ),
              drop: 10,
            },
          ]}
        />
      </div>
      <Async r={list}>
        {(page) =>
          page.data.length === 0 ? (
            <EmptyState title={t("issues.empty")} />
          ) : (
            <Paged page={page} size={PAGE_SIZE} go={(cursor) => setQuery({ cursor })}>
              <Table stack={true}>
                <thead class="bg-zinc-50">
                  <tr>
                    <Th>{t("issues.severity")}</Th>
                    <Th>{t("issues.message")}</Th>
                    <Th>{t("issues.item")}</Th>
                    <Th>{t("issues.source")}</Th>
                    <Th>
                      <span class="sr-only">{t("issues.actionsColumn")}</span>
                    </Th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-zinc-100">
                  {page.data.map((x, i) => (
                    <tr key={x.id ?? `${x.item}:${x.path}:${i}`}>
                      <Td label={t("issues.severity")}>
                        <SeverityLabel severity={x.severity} />
                      </Td>
                      <Td>
                        <RichText space={space} text={x.message} inline={true} />
                        {x.id ? (
                          <CommentCount
                            count={commentCount(space.id, "findings", x.id)}
                            href={spaceUrl(space, `/items/${x.item}?tab=conversation`)}
                          />
                        ) : null}
                      </Td>
                      {/* As lists show items: its kind (the side panel's button), its title, its entity below. */}
                      <Td label={t("issues.item")}>
                        <ItemLines
                          space={space}
                          item={{ id: x.item, title: x.item_title, ...(pick(x.item) ?? {}) }}
                          entity={entity ? undefined : (entities.data?.find((e) => e.id === x.item_entity) ?? null)}
                        />
                      </Td>
                      <Td label={t("issues.source")}>
                        {x.source === "review" ? (
                          <>
                            {x.reviewer_name ?? t("issues.reviewer")}
                            {/* The review it came from: what else it found, what it relied on. */}
                            {x.review ? (
                              <Link href={spaceUrl(space, `/reviews/${x.review}`)} class={`${LINK} block text-xs`}>
                                {t("issues.viewReview")}
                              </Link>
                            ) : null}
                          </>
                        ) : (
                          t("issues.schema")
                        )}
                      </Td>
                      <Td class="w-px" label="">
                        {x.review && x.id && canEdit(space) ? (
                          <FindingActions space={space} item={x.item} issue={x} onChange={list.reload} />
                        ) : null}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Paged>
          )
        }
      </Async>
    </>
  );
}
